import { isResponseWithData } from 'lib-shared-auth';
import { useEffect, useMemo, useState } from 'react';

import {
  aiAgentsApi,
  IntegrationActionRun,
  IntegrationActionRunArrayItem,
  isIntegrationActionRunArrayItem,
} from 'apis/aiAgents';
import {
  CustomFieldAttribute,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { convertTreeToFlatItems } from 'utils/tree';

type UseCustomAttributesSuggestionsOptions = {
  customAttributes: CustomFieldAttribute[];
  csid: number;
};

export type CustomAttributeOption = {
  label: string;
  value: string[];
};

type FlattenAttribute = {
  options: Record<string, CustomAttributeOption>;
};

function getFlattenOptions(
  options: readonly CustomFieldSelectAttributeOption[],
): Record<string, CustomAttributeOption> {
  return convertTreeToFlatItems(options, {
    childrenKey: 'options',
    transformItem: (option, parentOptions) => {
      const label = parentOptions
        .map((node) => node.label)
        .concat(option.label)
        .join(' / ');
      const value = parentOptions
        .map((node) => node.value)
        .concat(option.value);
      return {
        id: option.id,
        label,
        value,
      };
    },
  }).reduce(
    (acc, { id, ...option }) => ({
      ...acc,
      [id]: option,
    }),
    {} as Record<string, CustomAttributeOption>,
  );
}

function getSuggestionsPerCustomAttributes(
  rawSuggestions: readonly IntegrationActionRun[],
  flattenAttributes: Record<string, FlattenAttribute>,
): Record<string, CustomAttributeOption[]> {
  return rawSuggestions
    .filter(
      (suggestion): suggestion is IntegrationActionRunArrayItem =>
        isIntegrationActionRunArrayItem(suggestion) &&
        suggestion.value.length > 0 &&
        !!flattenAttributes[suggestion.name],
    )
    .map((suggestion) => {
      const attribute = flattenAttributes[suggestion.name];
      // For non-select attributes the label and value are the same, and the value has only string
      if (Object.keys(attribute.options).length === 0) {
        return {
          id: suggestion.name,
          suggestions: [
            {
              label: suggestion.value[0],
              value: [...suggestion.value],
            },
          ],
        };
      }
      const mappedSuggestions = suggestion.value
        .map((optionId) => attribute.options?.[optionId])
        .filter((option): option is CustomAttributeOption => !!option);
      return { id: suggestion.name, suggestions: mappedSuggestions };
    })
    .reduce(
      (acc, { id, suggestions }) =>
        suggestions.length > 0 ? { ...acc, [id]: suggestions } : acc,
      {} as Record<string, CustomAttributeOption[]>,
    );
}

export function useCustomAttributeSuggestions({
  customAttributes,
  csid,
}: UseCustomAttributesSuggestionsOptions): Record<
  string,
  CustomAttributeOption[]
> {
  const [triggerSuggestCustomAttributesRun] =
    aiAgentsApi.useCreateToolsIntegrationsActionsRunMutation();
  const [suggestionsPerCustomAttribute, setSuggestionsPerCustomAttribute] =
    useState<Record<string, CustomAttributeOption[]>>({});

  const { customAttributesSuggestions } = useFeatureFlag([
    'custom-attributes-suggestions',
  ]);

  const flattenAttributes = useMemo(
    () =>
      customAttributes
        .map((customAttribute) => {
          const options =
            customAttribute.type === 'select' ? customAttribute.options : [];
          return {
            id: customAttribute.id,
            options: getFlattenOptions(options),
          };
        })
        .reduce(
          (acc, { id, options }) => ({ ...acc, [id]: { options } }),
          {} as Record<string, FlattenAttribute>,
        ),
    [customAttributes],
  );

  useEffect(() => {
    if (!customAttributesSuggestions) {
      setSuggestionsPerCustomAttribute({});
      return;
    }
    (async () => {
      const response = await triggerSuggestCustomAttributesRun({
        integrationType: 'dixa',
        actionId: 'suggest_conversation_custom_attributes',
        csid,
      });
      if (!isResponseWithData(response)) return;

      setSuggestionsPerCustomAttribute(
        getSuggestionsPerCustomAttributes(
          response.data.items,
          flattenAttributes,
        ),
      );
    })();
  }, [
    csid,
    customAttributesSuggestions,
    flattenAttributes,
    triggerSuggestCustomAttributesRun,
  ]);

  return suggestionsPerCustomAttribute;
}
